import * as React from "react";
import { FieldValues, RegisterOptions } from "react-hook-form";
import { FormTextField, FormTextFieldProps } from "@tlc/forms/FormTextField";
import { useLanguage } from "../../Language";

interface PhoneFieldProps<T> extends Omit<FormTextFieldProps<T>, "options"> {
  options?: Omit<RegisterOptions<T>, "valueAsDate" | "valueAsNumber">;
}

export const PhoneField = <T extends FieldValues>({
  label,
  options,
  ...props
}: PhoneFieldProps<T>) => {
  const { translate } = useLanguage();
  const PHONE_PATTERN = /^\(?(\d{3})\)?[-.\s]?(\d{3})[-.\s ]?(\d{4})$/;
  const mergedOptions: Partial<RegisterOptions<T>> = {
    ...(options || {}),
    setValueAs: (value) => {
      if (typeof value !== "string") return value; // Return unchanged if not a string

      const rawValue = value.trim(); // Trim spaces but keep all characters
      const digitsOnly = rawValue.replace(/\D/g, ""); // Remove non-digits

      // If the input matches the valid phone pattern, return the cleaned number
      if (PHONE_PATTERN.test(rawValue)) {
        return digitsOnly;
      }

      // If an invalid phone pattern, return the raw input so validation can be displayed
      return rawValue;
    },
    pattern: {
      value: PHONE_PATTERN,
      message: translate("invalidFormatError", {
        LABEL: label,
      }),
    },
  };

  return (
    <FormTextField<T>
      {...props}
      label={label}
      options={mergedOptions}
      type="tel"
      maxLength={16}
    />
  );
};
